/**
 * Safe print utility - uses only DOM manipulation to prevent XSS vulnerabilities
 * No document.write() or innerHTML with untrusted content
 */

/**
 * Creates a complete print document using only DOM manipulation
 * @param {Document} doc - The document to populate
 * @param {string} styles - CSS styles as text
 */
const applyStyles = (doc, styles) => {
  const styleEl = doc.createElement('style');
  styleEl.textContent = styles;
  doc.head.appendChild(styleEl);
};

/**
 * Default print styles
 */
const DEFAULT_STYLES = `
  body { 
    font-family: 'Georgia', serif; 
    padding: 40px; 
    max-width: 800px; 
    margin: 0 auto;
    color: #333;
  }
  h1 { color: #5A6B47; font-size: 24px; margin-bottom: 10px; }
  h2 { color: #333; font-size: 18px; margin-top: 20px; }
  h3 { color: #5A6B47; font-size: 16px; }
  table { width: 100%; border-collapse: collapse; margin: 15px 0; }
  th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
  th { background: #f5f5f0; color: #333; font-weight: 600; }
  .header { border-bottom: 2px solid #5A6B47; padding-bottom: 15px; margin-bottom: 20px; }
  .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; }
  .badge-green { background: #5A6B47; color: white; }
  .badge-orange { background: #C4704F; color: white; }
  .info-box { background: #f5f5f0; padding: 15px; border-radius: 8px; margin: 10px 0; }
  .print-date { color: #666; font-size: 12px; margin-top: 30px; }
  .info { margin: 5px 0; }
  img { max-width: 150px; border-radius: 8px; }
  @media print { body { padding: 20px; } }
`;

/**
 * Sanitizes and clones an element for printing
 * Removes scripts and event handlers
 * @param {HTMLElement} element - Element to clone safely
 * @returns {HTMLElement} - Sanitized clone
 */
const safeCloneElement = (element) => {
  const clone = element.cloneNode(true);
  
  // Remove all script elements
  const scripts = clone.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  
  // Remove event handler attributes
  const allElements = clone.querySelectorAll('*');
  allElements.forEach(el => {
    // Remove on* attributes
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
    });
  });
  
  return clone;
};

/**
 * Creates a safe print window with proper DOM manipulation
 * @param {Object} options - Print options
 * @param {string} options.title - Document title
 * @param {string} options.subtitle - Optional subtitle
 * @param {HTMLElement} options.content - Content element to print
 * @param {string} options.styles - Additional CSS styles
 */
export const safePrint = ({ title, subtitle, content, styles = '' }) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    console.error('Failed to open print window - popup may be blocked');
    return;
  }

  const doc = printWindow.document;
  
  // Create HTML structure using DOM methods only
  const html = doc.documentElement;
  html.lang = 'en';
  
  // Clear and setup head
  while (doc.head.firstChild) {
    doc.head.removeChild(doc.head.firstChild);
  }
  
  // Set title safely using textContent
  const titleEl = doc.createElement('title');
  titleEl.textContent = `${title || 'Print'} - The Pack Standard`;
  doc.head.appendChild(titleEl);
  
  // Add meta charset
  const meta = doc.createElement('meta');
  meta.setAttribute('charset', 'UTF-8');
  doc.head.appendChild(meta);
  
  // Apply styles
  applyStyles(doc, DEFAULT_STYLES + styles);
  
  // Clear and setup body
  while (doc.body.firstChild) {
    doc.body.removeChild(doc.body.firstChild);
  }
  
  // Create header
  const header = doc.createElement('div');
  header.className = 'header';
  
  const h1 = doc.createElement('h1');
  h1.textContent = 'The Pack Standard - Puppy Academy';
  header.appendChild(h1);
  
  if (subtitle) {
    const p = doc.createElement('p');
    p.style.cssText = 'color: #666; margin: 0;';
    p.textContent = subtitle;
    header.appendChild(p);
  }
  
  doc.body.appendChild(header);
  
  // Add content safely
  if (content instanceof HTMLElement) {
    const safeContent = safeCloneElement(content);
    doc.body.appendChild(safeContent);
  }
  
  // Add print date
  const printDate = doc.createElement('p');
  printDate.className = 'print-date';
  printDate.textContent = `Printed on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`;
  doc.body.appendChild(printDate);
  
  // Trigger print after content loads
  setTimeout(() => {
    printWindow.print();
  }, 100);
};

/**
 * Print intake details safely using only DOM manipulation
 * @param {Object} intake - Intake object
 * @param {Array} attendees - List of enrolled attendees
 */
export const printIntakeDetails = (intake, attendees) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    console.error('Failed to open print window - popup may be blocked');
    return;
  }

  const doc = printWindow.document;
  
  // Setup document
  const titleEl = doc.createElement('title');
  titleEl.textContent = `Intake Details - ${intake.program_name || 'Unknown'}`;
  doc.head.appendChild(titleEl);
  
  applyStyles(doc, `
    body { font-family: Georgia, serif; padding: 40px; color: #333; }
    h1, h2, h3 { color: #556B2F; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
    th { background: #f5f5f0; }
    .header { margin-bottom: 30px; }
    .info { margin: 5px 0; }
  `);
  
  // Clear body
  while (doc.body.firstChild) {
    doc.body.removeChild(doc.body.firstChild);
  }
  
  // Header
  const header = doc.createElement('div');
  header.className = 'header';
  
  const h1 = doc.createElement('h1');
  h1.textContent = 'The Pack Standard';
  header.appendChild(h1);
  
  const subtitleP = doc.createElement('p');
  subtitleP.textContent = 'Puppy Academy - Intake Details';
  header.appendChild(subtitleP);
  
  doc.body.appendChild(header);
  
  // Intake info
  const h2 = doc.createElement('h2');
  h2.textContent = intake.program_name || 'Program';
  doc.body.appendChild(h2);
  
  // Helper to create info paragraphs safely
  const createInfo = (label, value) => {
    const p = doc.createElement('p');
    p.className = 'info';
    const strong = doc.createElement('strong');
    strong.textContent = `${label}: `;
    p.appendChild(strong);
    p.appendChild(doc.createTextNode(String(value || 'N/A')));
    return p;
  };
  
  doc.body.appendChild(createInfo('Start Date', intake.start_date));
  doc.body.appendChild(createInfo('End Date', intake.end_date));
  doc.body.appendChild(createInfo('Day', intake.day_of_week ? `${intake.day_of_week}s` : 'N/A'));
  doc.body.appendChild(createInfo('Capacity', `${attendees.length}/${intake.max_puppies || 6}`));
  
  // Attendees section
  const h3 = doc.createElement('h3');
  h3.textContent = `Enrolled Puppies (${attendees.length})`;
  doc.body.appendChild(h3);
  
  if (attendees.length === 0) {
    const noEnroll = doc.createElement('p');
    noEnroll.textContent = 'No enrollments yet';
    doc.body.appendChild(noEnroll);
  } else {
    const table = doc.createElement('table');
    const thead = doc.createElement('thead');
    const headerRow = doc.createElement('tr');
    
    ['Puppy', 'Owner', 'Status', 'Sessions'].forEach(text => {
      const th = doc.createElement('th');
      th.textContent = text;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);
    
    const tbody = doc.createElement('tbody');
    attendees.forEach(e => {
      const tr = doc.createElement('tr');
      const cellValues = [
        e.puppy_name || 'Unknown',
        e.owner_name || 'N/A',
        e.status || 'Unknown',
        `${e.sessions_completed || 0}/4`
      ];
      
      cellValues.forEach(text => {
        const td = doc.createElement('td');
        td.textContent = String(text);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    doc.body.appendChild(table);
  }
  
  // Print date
  const printDate = doc.createElement('p');
  printDate.style.cssText = 'color: #666; font-size: 12px; margin-top: 30px;';
  printDate.textContent = `Printed on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`;
  doc.body.appendChild(printDate);
  
  setTimeout(() => {
    printWindow.print();
  }, 100);
};

/**
 * Print element content safely
 * @param {string} elementId - ID of element to print
 * @param {string} title - Print title
 */
export const printElementById = (elementId, title) => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    return;
  }
  
  safePrint({
    title,
    subtitle: title,
    content: element
  });
};
